import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 8;

function unique(list: string[]) {
  return Array.from(new Set(list));
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get("type") === "tv" ? "tv" : searchParams.get("type") === "movie" ? "movie" : "all";
  const q = searchParams.get("q") || "";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const genre = searchParams.get("genre") || "全部";
  const sort = searchParams.get("sort") || "latest";

  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = window.setTimeout(() => setLoading(false), 400);
    return () => window.clearTimeout(t);
  }, [type, q, region, year, genre, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, q, region, year, genre, sort]);

  const accessibleGenres = useMemo(
    () => unique(movies.filter((m) => (type === "all" ? true : m.type === type)).flatMap((m) => m.genres)),
    [type]
  );
  const accessibleYears = useMemo(
    () => unique(movies.filter((m) => (type === "all" ? true : m.type === type)).map((m) => String(m.year))).sort((a, b) => Number(b) - Number(a)),
    [type]
  );
  const accessibleRegions = useMemo(
    () => unique(movies.filter((m) => (type === "all" ? true : m.type === type)).map((m) => m.region)),
    [type]
  );

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      if (type !== "all" && m.type !== type) return false;
      if (region !== "全部" && m.region !== region) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      if (genre !== "全部" && !m.genres.includes(genre)) return false;
      if (q && !m.title.includes(q) && !m.summary.includes(q)) return false;
      return true;
    });
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === "hot") list = [...list].sort((a, b) => b.rating - a.rating || b.year - a.year);
    else list = [...list].sort((a, b) => b.year - a.year);
    return list;
  }, [type, q, region, year, genre, sort]);

  const shown = filtered.slice(0, visible);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部" || (key === "sort" && value === "latest")) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilter = q || region !== "全部" || year !== "全部" || genre !== "全部" || type !== "all";

  const pageTitle = type === "tv" ? "电视剧分类" : type === "movie" ? "电影分类" : "影视分类";
  const pageDesc =
    type === "tv"
      ? "按地区、年份与类型筛选电视剧，查看剧情简介，找到下一部要追的剧。"
      : type === "movie"
      ? "按地区、年份与类型筛选电影，浏览剧情介绍，快速挑选今晚想看的片子。"
      : "浏览全部电影与电视剧，支持地区、年份、类型组合筛选与关键词搜索。";

  const filterGroups = [
    { key: "region", label: "地区", options: ["全部", ...accessibleRegions], value: region },
    { key: "year", label: "年份", options: ["全部", ...accessibleYears], value: year },
    { key: "genre", label: "类型", options: ["全部", ...accessibleGenres], value: genre },
  ];

  const renderFilters = () => (
    <div className="flex flex-col gap-5">
      {filterGroups.map((group) => (
        <div key={group.key} className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm font-medium text-foreground-700 shrink-0 pt-1.5 md:w-12">{group.label}</span>
          <div className="flex flex-wrap gap-1.5 md:gap-2">
            {group.options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => updateParam(group.key, opt)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  group.value === opt
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
        <span className="text-sm font-medium text-foreground-700 shrink-0 pt-1.5 md:w-12">排序</span>
        <div className="flex flex-wrap gap-1.5 md:gap-2">
          {[
            { k: "latest", l: "最新上映" },
            { k: "rating", l: "评分最高" },
            { k: "hot", l: "最热门" },
          ].map((s) => (
            <button
              key={s.k}
              type="button"
              onClick={() => updateParam("sort", s.k)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sort === s.k ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-200"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  useDocumentTitle(`${pageTitle} - 诱人的飞行`);

  return (
    <div className="w-full">
      {/* 页头 */}
      <div className="w-full bg-primary-900 py-8 md:py-12 px-4 md:px-8">
        <nav className="text-xs text-background-300 mb-3 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-background-100">{pageTitle}</span>
          {q && (
            <>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-accent-400">搜索“{q}”</span>
            </>
          )}
        </nav>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-2">{pageTitle}</h1>
        <p className="text-sm text-background-300 max-w-2xl">{pageDesc}</p>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        {/* 子分类导航 */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="inline-flex items-center px-1 py-1 rounded-full bg-background-100 border border-background-200">
            {[
              { k: "all", l: "全部" },
              { k: "movie", l: "电影" },
              { k: "tv", l: "电视剧" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => updateParam("type", t.k)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  type === t.k ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-600">共 <strong className="text-foreground-950">{filtered.length}</strong> 部作品</span>
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="lg:hidden px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-filter-3-line" aria-hidden="true"></i>
              筛选
            </button>
          </div>
        </div>

        {/* 桌面筛选栏 */}
        <div className="hidden lg:block p-5 rounded-lg bg-background-50 border border-background-200 mb-7">
          {renderFilters()}
        </div>

        {/* 列表 */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-background-200 overflow-hidden animate-pulse">
                <div className="w-full aspect-[2/3] bg-background-200"></div>
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-background-200 rounded w-3/4"></div>
                  <div className="h-3 bg-background-200 rounded w-1/2"></div>
                  <div className="h-3 bg-background-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400 mb-4 inline-block" aria-hidden="true"></i>
            <p className="text-foreground-700 mb-1">没有找到符合条件的影视内容</p>
            <p className="text-sm text-foreground-500 mb-5">试着调整筛选条件，或重置后重新查找</p>
            <button
              type="button"
              onClick={resetAll}
              className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {shown.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="px-6 py-2.5 rounded-md bg-background-100 border border-background-200 hover:border-primary-300 text-sm font-medium text-foreground-800 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-arrow-down-line" aria-hidden="true"></i>
                  加载更多
                </button>
              </div>
            )}
          </>
        )}

        {hasActiveFilter && !loading && filtered.length > 0 && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={resetAll}
              className="text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              清除全部筛选
            </button>
          </div>
        )}
      </div>

      {/* 移动端全屏筛选面板 */}
      {mobileFilterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex flex-col bg-background-50 animate-fade-in">
          <div className="flex items-center justify-between px-4 h-16 border-b border-background-200">
            <h2 className="font-heading font-bold text-lg text-foreground-950">筛选条件</h2>
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="关闭筛选"
            >
              <i className="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">{renderFilters()}</div>
          <div className="p-4 border-t border-background-200 flex gap-3">
            <button
              type="button"
              onClick={() => {
                resetAll();
                setMobileFilterOpen(false);
              }}
              className="flex-1 py-3 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="flex-1 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}